import { useState } from "react";
import { Calendar, AlertCircle, Plus } from "lucide-react";
import { Button } from "@cieda/djayma-ui";
import { toast } from "sonner";
import AdminLayout from "@/components/admin/AdminLayout";
import { AdminModal, EmptyState } from "@/components/admin/shared";
import {
  useSeasonalEvents,
  useCreateSeasonalEvent,
} from "@/hooks/admin/useMarketing";
import type { CreateSeasonalInput } from "@/lib/admin-api/marketing";

// ---------------------------------------------------------------------------
// Create event modal
// ---------------------------------------------------------------------------

function CreateEventModal({
  open,
  onClose,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
}) {
  const create = useCreateSeasonalEvent();
  const [name, setName] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [description, setDescription] = useState("");

  const handleSubmit = () => {
    if (!name.trim()) { toast.error("Le nom est obligatoire."); return; }
    if (!startDate || !endDate) { toast.error("Les dates sont obligatoires."); return; }

    const input: CreateSeasonalInput = {
      name,
      start_date: startDate,
      end_date: endDate,
      description,
    };
    create.mutate(input, {
      onSuccess: () => { toast.success("Événement créé."); onClose(); },
      onError: () => toast.error("Erreur lors de la création de l'événement."),
    });
  };

  return (
    <AdminModal
      open={open}
      onClose={onClose}
      title="Nouvel événement saisonnier"
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={handleSubmit} disabled={create.isPending}>
            {create.isPending ? "En cours..." : "Créer"}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2 space-y-1">
          <label className="text-sm font-medium text-stone-700">
            Nom <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="ex : Tabaski 2026"
            className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
          />
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium text-stone-700">
            Date de début <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
          />
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium text-stone-700">
            Date de fin <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
          />
        </div>
        <div className="sm:col-span-2 space-y-1">
          <label className="text-sm font-medium text-stone-700">
            Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            placeholder="Notes et contexte pour cet événement"
            className="w-full resize-none rounded-lg border border-stone-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
          />
        </div>
      </div>
    </AdminModal>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function Seasonal() {
  const [modalOpen, setModalOpen] = useState(false);
  const { data, isLoading, isError } = useSeasonalEvents();
  const events = data?.events ?? [];

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
              <Calendar className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold text-stone-900">
                Calendrier saisonnier
              </h1>
              <p className="text-sm text-stone-500">
                Fêtes, événements et campagnes planifiées
              </p>
            </div>
          </div>
          <Button onClick={() => setModalOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Nouvel événement
          </Button>
        </div>

        {/* Content */}
        {isLoading && (
          <div className="space-y-3 animate-pulse">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 rounded bg-stone-100" />
            ))}
          </div>
        )}

        {isError && (
          <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 shrink-0" />
            Impossible de charger le calendrier.
          </div>
        )}

        {!isLoading && !isError && events.length === 0 && (
          <EmptyState
            icon={<Calendar className="h-6 w-6" />}
            title="Aucun événement planifié"
            description="Planifiez vos campagnes saisonnières : Tabaski, Korité, Noël, etc."
            action={{ label: "Nouvel événement", onClick: () => setModalOpen(true) }}
          />
        )}

        {!isLoading && !isError && events.length > 0 && (
          <div className="overflow-hidden rounded-xl border border-stone-200 bg-white">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-stone-100 bg-stone-50">
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Événement</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Début</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Fin</th>
                    <th className="hidden md:table-cell px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Description</th>
                    <th className="hidden sm:table-cell px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-stone-500">Promos liées</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {events.map((ev) => {
                    const today = new Date();
                    const start = new Date(ev.start_date);
                    const end = new Date(ev.end_date);
                    const isActive = today >= start && today <= end;
                    const isUpcoming = today < start;

                    return (
                      <tr key={ev.id} className="hover:bg-stone-50 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span
                              className={`inline-block h-2 w-2 rounded-full ${
                                isActive
                                  ? "bg-emerald-500"
                                  : isUpcoming
                                  ? "bg-blue-400"
                                  : "bg-stone-300"
                              }`}
                            />
                            <span className="font-medium text-stone-900">
                              {ev.name}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-stone-600">
                          {new Date(ev.start_date).toLocaleDateString("fr-SN")}
                        </td>
                        <td className="px-4 py-3 text-stone-600">
                          {new Date(ev.end_date).toLocaleDateString("fr-SN")}
                        </td>
                        <td className="hidden md:table-cell px-4 py-3 text-stone-500 max-w-xs">
                          <span className="line-clamp-1">
                            {ev.description || "—"}
                          </span>
                        </td>
                        <td className="hidden sm:table-cell px-4 py-3 text-right tabular-nums text-stone-600">
                          {ev.linked_promotions.length}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <CreateEventModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </AdminLayout>
  );
}
